import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import {
  ArrowLeft,
  Download,
  ExternalLink,
  FileText,
  FlaskConical,
  Receipt,
  Trash2,
  User,
} from "lucide-react";

import API from "../lib/api";

import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

import DataTable from "../components/common/DataTable";
import StatCard from "../components/common/StatCard";

import { erpApi, formatCurrency, formatDate } from "../services/erpApi";

export default function PatientDetail() {
  const { id } = useParams();

  const [patient, setPatient] = useState(null);
  const [documentOpen, setDocumentOpen] = useState(false);
  const [documentForm, setDocumentForm] = useState({
    title: "",
    document_type: "document",
    document_url: "",
    remarks: "",
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadPatient() {
      try {
        const response = await erpApi.patient(id);

        setPatient(response.data);
      } catch {
        toast.error("Failed to load patient profile");
      } finally {
        setLoading(false);
      }
    }

    loadPatient();
  }, [id]);

  const reloadPatient = async () => {
    const response = await erpApi.patient(id);

    setPatient(response.data);
  };

  const deleteBill = async (bill) => {
    const ok = window.confirm(
      `Delete invoice ${
        bill.invoice_no || `#${bill.id.slice(0, 8).toUpperCase()}`
      } ?`,
    );

    if (!ok) return;

    try {
      await erpApi.deleteBill(bill.id);

      toast.success("Bill deleted");

      await reloadPatient();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete bill");
    }
  };

  const downloadPrescription = async (prescription) => {
    const response = await API.get(`/prescriptions/${prescription.id}/pdf`, {
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));

    const link = document.createElement("a");

    link.href = url;

    link.setAttribute(
      "download",
      `prescription_${prescription.id.slice(0, 8)}.pdf`,
    );

    document.body.appendChild(link);

    link.click();

    link.remove();
  };

  const deletePrescription = async (prescription) => {
    const ok = window.confirm("Delete this prescription?");

    if (!ok) return;

    try {
      await erpApi.deletePrescription(prescription.id);

      toast.success("Prescription deleted");

      await reloadPatient();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Failed to delete prescription",
      );
    }
  };

  const addDocument = async (event) => {
    event.preventDefault();
    if (!documentForm.title || !documentForm.document_url) {
      toast.error("Document title and URL are required");
      return;
    }
    try {
      await erpApi.addPatientDocument(id, documentForm);
      toast.success("Document attached");
      setDocumentOpen(false);
      setDocumentForm({ title: "", document_type: "document", document_url: "", remarks: "" });
      await reloadPatient();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to attach document");
    }
  };

  const deleteDocument = async (document) => {
    if (!window.confirm(`Delete document "${document.title}"?`)) return;
    try {
      await erpApi.deletePatientDocument(id, document.id);
      toast.success("Document deleted");
      await reloadPatient();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };

  if (loading)
    return <div className="h-64 animate-pulse rounded-lg bg-white" />;

  if (!patient) return <p>Patient not found</p>;

  const totalBilled = (patient.bills || []).reduce(
    (sum, bill) => sum + Number(bill.total || 0),
    0,
  );

  const pendingSamples = (patient.samples || []).filter(
    (sample) => !["printed", "delivered"].includes(sample.status),
  ).length;

  return (
    <div className="space-y-6">
      <Link
        to="/patients"
        className="inline-flex items-center text-sm text-hospital-text-muted hover:text-hospital-primary"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        Back to Patients
      </Link>

      {/* PROFILE HEADER */}

      <Card className="border border-gray-200">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              {patient.photo_url ? (
                <img
                  src={patient.photo_url}
                  alt={patient.name}
                  className="h-20 w-20 rounded-full border border-gray-200 object-cover"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-hospital-primary-light text-hospital-primary">
                  <User className="h-8 w-8" />
                </div>
              )}

              <div>
                <h1 className="font-manrope text-2xl font-bold">
                  {patient.name}
                </h1>

                <p className="text-sm text-hospital-text-muted">
                  {patient.patient_uid || patient.id.slice(0, 8)}
                  {" | "}
                  {patient.age} years
                  {" | "}
                  {patient.gender}
                  {" | "}
                  {patient.mobile}
                </p>
                {patient.relation_type && patient.relation_name && (
                  <p className="text-sm text-hospital-text-muted">
                    {patient.relation_type} {patient.relation_name}
                  </p>
                )}

                <div className="mt-2 space-y-1 text-sm text-hospital-text-muted">
                  <p>
                    {patient.address || "No address"}

                    {patient.city ? `, ${patient.city}` : ""}

                    {patient.state ? `, ${patient.state}` : ""}
                  </p>

                  <p>
                    Blood Group: {patient.blood_group || "-"}
                    {" | "}
                    Doctor:{" "}
                    {patient.doctor_assigned
                      ? `Dr. ${patient.doctor_assigned}`
                      : "-"}
                  </p>

                  <p>
                    Emergency: {patient.emergency_contact_name || "-"}
                    {" | "}
                    {patient.emergency_contact_mobile || "-"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Link to={`/billing/${patient.id}`}>
                <Button className="btn-primary">
                  <Receipt className="mr-2 h-4 w-4" />
                  New Bill
                </Button>
              </Link>

              <Link to="/prescriptions">
                <Button variant="outline">
                  <FileText className="mr-2 h-4 w-4" />
                  Prescription
                </Button>
              </Link>

              <Link to="/samples">
                <Button variant="outline">
                  <FlaskConical className="mr-2 h-4 w-4" />
                  New Sample
                </Button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* STATS */}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <StatCard
          title="Total Bills"
          value={(patient.bills || []).length}
          icon={Receipt}
        />

        <StatCard
          title="Total Billed"
          value={formatCurrency(totalBilled)}
          icon={Receipt}
          tone="success"
        />

        <StatCard
          title="Pending Samples"
          value={pendingSamples}
          icon={FlaskConical}
          tone="warning"
        />

        <StatCard
          title="Prescriptions"
          value={(patient.prescriptions || []).length}
          icon={FileText}
          to="/prescriptions"
        />
      </div>

      <Card className="border border-gray-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-manrope text-base">Patient Documents</CardTitle>
          <Button variant="outline" onClick={() => setDocumentOpen(true)}>Attach Document</Button>
        </CardHeader>
        <CardContent className="space-y-3">
          {(patient.documents || []).length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-hospital-text-muted">
              No uploaded documents yet. Add report links, old bills, ID proof or referral papers here.
            </p>
          ) : (
            (patient.documents || []).map((document) => (
              <div key={document.id} className="flex flex-col gap-3 rounded-lg border border-gray-200 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-semibold text-hospital-text-main">{document.title}</p>
                  <p className="text-xs capitalize text-hospital-text-muted">{document.document_type} · {formatDate(document.created_at)} · {document.uploaded_by || "Staff"}</p>
                  {document.remarks && <p className="mt-1 text-sm text-hospital-text-muted">{document.remarks}</p>}
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" asChild>
                    <a href={document.document_url} target="_blank" rel="noreferrer" title="Open document">
                      <ExternalLink className="h-4 w-4 text-hospital-primary" />
                    </a>
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteDocument(document)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* PROFILE + TABLES */}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        {/* MEDICAL PROFILE */}

        <Card className="border border-gray-200">
          <CardHeader>
            <CardTitle className="font-manrope text-base">
              Patient Medical Profile
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-hospital-text-muted">Aadhaar</p>

                <p className="font-medium">{patient.aadhaar_number || "-"}</p>
              </div>

              <div>
                <p className="text-hospital-text-muted">Occupation</p>

                <p className="font-medium">{patient.occupation || "-"}</p>
              </div>

              <div>
                <p className="text-hospital-text-muted">Alternate Mobile</p>

                <p className="font-medium">{patient.alternate_mobile || "-"}</p>
              </div>

              <div>
                <p className="text-hospital-text-muted">Relative</p>

                <p className="font-medium">
                  {patient.relation_type && patient.relation_name
                    ? `${patient.relation_type} ${patient.relation_name}`
                    : "-"}
                </p>
              </div>

              <div>
                <p className="text-hospital-text-muted">Referred By</p>

                <p className="font-medium">{patient.referred_by || "-"}</p>
              </div>
            </div>

            <div>
              <p className="text-hospital-text-muted">Medical History</p>

              <p className="font-medium">{patient.medical_history || "-"}</p>
            </div>

            <div>
              <p className="text-hospital-text-muted">Allergies</p>

              <p className="font-medium">{patient.allergies || "-"}</p>
            </div>

            <div>
              <p className="text-hospital-text-muted">Notes</p>

              <p className="font-medium">{patient.notes || "-"}</p>
            </div>
          </CardContent>
        </Card>

        {/* BILLING HISTORY */}

        <DataTable
          title="Billing History"
          rows={patient.bills || []}
          empty="No bills for this patient"
          columns={[
            {
              key: "invoice_no",

              label: "Invoice",

              render: (row) =>
                row.invoice_no || `#${row.id.slice(0, 8).toUpperCase()}`,
            },

            {
              key: "created_at",

              label: "Date",

              render: (row) => formatDate(row.created_at),
            },

            {
              key: "status",

              label: "Status",

              render: (row) => <span className="capitalize">{row.status}</span>,
            },

            {
              key: "total",

              label: "Total",

              align: "right",

              render: (row) => formatCurrency(row.total),
            },

            {
              key: "due_amount",

              label: "Due",

              align: "right",

              render: (row) => formatCurrency(row.due_amount || 0),
            },

            {
              key: "payment_history",

              label: "Payments",

              render: (row) => `${row.payment_history?.length || 0}`,
            },

            {
              key: "delete",

              label: "",

              render: (row) => (
                <Button
                  variant="ghost"
                  size="icon"
                  title="Delete bill"
                  onClick={() => deleteBill(row)}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              ),
            },
          ]}
        />

        {/* LAB HISTORY */}

        <DataTable
          title="Lab & Sample History"
          rows={patient.samples || []}
          empty="No samples for this patient"
          columns={[
            {
              key: "tests",

              label: "Tests",

              render: (row) =>
                row.tests?.length
                  ? row.tests.map((t) => t.test_name).join(", ")
                  : row.test_name || "-",
            },

            {
              key: "sample_type",

              label: "Sample",

              render: (row) => row.sample_type || "-",
            },

            {
              key: "status",

              label: "Status",

              render: (row) => (
                <span className="capitalize">
                  {row.status?.replaceAll("_", " ")}
                </span>
              ),
            },

            {
              key: "expected_date",

              label: "Sample Taken",

              render: (row) => formatDate(row.collection_date),
            },
          ]}
        />

        {/* PRESCRIPTION HISTORY */}

        <DataTable
          title="Prescription History"
          rows={patient.prescriptions || []}
          empty="No prescriptions for this patient"
          columns={[
            {
              key: "created_at",

              label: "Date",

              render: (row) => formatDate(row.created_at),
            },

            {
              key: "doctor_name",

              label: "Doctor",
            },

            {
              key: "diagnosis",

              label: "Diagnosis",

              render: (row) => row.diagnosis || "-",
            },

            {
              key: "medicines",

              label: "Medicines",

              render: (row) => `${row.medicines?.length || 0}`,
            },

            {
              key: "actions",

              label: "",

              render: (row) => (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => downloadPrescription(row)}
                  >
                    <Download className="h-4 w-4 text-hospital-primary" />
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deletePrescription(row)}
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              ),
            },
          ]}
        />
      </div>

      {/* TIMELINE */}

      <Card className="border border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-manrope text-base">
            <FileText className="h-4 w-4" />
            Patient Timeline
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-3">
          {(patient.timeline || []).length === 0 ? (
            <p className="text-sm text-hospital-text-muted">No activity yet</p>
          ) : (
            patient.timeline.map((item, index) => (
              <div
                key={index}
                className="rounded-lg border border-gray-200 p-3"
              >
                <p className="font-medium">{item.title}</p>

                <p className="text-xs text-hospital-text-muted">
                  {formatDate(item.date)}
                  {" | "}
                  {item.type}
                </p>
              </div>
            ))
          )}
        </CardContent>
      </Card>
      <Dialog open={documentOpen} onOpenChange={setDocumentOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>Attach Patient Document</DialogTitle></DialogHeader>
          <form onSubmit={addDocument} className="space-y-3">
            <div className="space-y-2"><Label>Title</Label><Input value={documentForm.title} onChange={(e) => setDocumentForm({ ...documentForm, title: e.target.value })} placeholder="CBC report, old bill, Aadhaar, referral note" /></div>
            <div className="space-y-2"><Label>Document Type</Label><Input value={documentForm.document_type} onChange={(e) => setDocumentForm({ ...documentForm, document_type: e.target.value })} placeholder="report, bill, id proof, referral" /></div>
            <div className="space-y-2"><Label>Document URL</Label><Input value={documentForm.document_url} onChange={(e) => setDocumentForm({ ...documentForm, document_url: e.target.value })} placeholder="https://..." /></div>
            <div className="space-y-2"><Label>Remarks</Label><Input value={documentForm.remarks} onChange={(e) => setDocumentForm({ ...documentForm, remarks: e.target.value })} /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDocumentOpen(false)}>Cancel</Button><Button className="btn-primary">Save Document</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
